'use client'

import type { ChainId } from '@morpho-org/blue-sdk'
import type { z } from 'zod'

import { Tag } from '@morpho-org/ui-v6/server'
import { type Address, isAddressEqual } from 'viem'

import type { VaultV2Roles } from '@/lib/procedures/vaultV2Roles.contract'

import { getSafeQuorum, hasAragonMetadata } from '@/helpers/quorum'
import { VaultV2TimelockedFunctionName } from '@/types/enums'

import type { VaultV2PendingTimelock } from '../../types/pendingTimelocks'
import type { pendingAllocatorChangeSchema } from './pendingAllocatorChanges.contract'

import AddressWithCopyAndExplorer from '../../components/AddressWithCopyAndExplorer'
import VaultV2PendingTimelocksIndicator from '../../components/VaultV2PendingTimelocksIndicator'
import { VaultV2RowList, VaultV2RowListRow } from '../../components/VaultV2RowList'

interface RiskCurationClientProps {
  chainId: ChainId
  allocators: VaultV2Roles['allocators']
  /** Vault V2 Public Allocator address when it holds the allocator role, otherwise `null`. */
  publicAllocator: Address | null
  /** Pending allocator role changes, in execution order; empty when Core could not answer. */
  pendingAllocatorChanges: { items: PendingAllocatorChange[]; partial: boolean }
}

type PendingAllocatorChange = z.infer<typeof pendingAllocatorChangeSchema>

const toPendingTimelock = (change: PendingAllocatorChange): VaultV2PendingTimelock => ({
  functionName: VaultV2TimelockedFunctionName.SetIsAllocator,
  validAt: change.executableAt,
  txHash: change.txHash,
  decodedData: {
    __typename: 'VaultV2SetIsAllocatorPendingData',
    account: { address: change.account },
    isAllocator: change.isAllocator
  }
})

const RiskCurationClient = ({
  chainId,
  allocators,
  publicAllocator,
  pendingAllocatorChanges
}: RiskCurationClientProps) => {
  const getAllocatorPendingTimelocks = (allocatorAddress: Address) =>
    pendingAllocatorChanges.items
      .filter(change => isAddressEqual(change.account, allocatorAddress))
      .map(toPendingTimelock)

  const allocatorsMetadata = allocators
    .map(allocator => ({
      ...allocator,
      safeQuorum: getSafeQuorum(allocator.metadata),
      isAragonDAO: hasAragonMetadata(allocator.metadata),
      isPublicAllocator:
        publicAllocator !== null && isAddressEqual(allocator.address, publicAllocator)
    }))
    .toSorted((a, b) => Number(a.isPublicAllocator) - Number(b.isPublicAllocator))

  if (allocatorsMetadata.length === 0) return null

  return (
    <VaultV2RowList>
      {allocatorsMetadata.map((allocator, index) => {
        const allocatorPendingTimelocks = getAllocatorPendingTimelocks(allocator.address)

        return (
          <VaultV2RowListRow
            key={index}
            label={`Allocator ${index + 1}`}
            tooltip={
              index === 0
                ? "Executes liquidity movements within curator-approved constraints. Has no ownership, custody, or control over depositors' assets. Multiple addresses can hold this role."
                : undefined
            }
            labelAddon={
              <>
                {allocator.isPublicAllocator && <Tag variant="main" label="Public Allocator" />}
                {allocatorPendingTimelocks.length > 0 && (
                  <VaultV2PendingTimelocksIndicator pendingTimelocks={allocatorPendingTimelocks} />
                )}
              </>
            }
          >
            <AddressWithCopyAndExplorer
              address={allocator.address}
              chainId={chainId}
              safeQuorum={allocator.safeQuorum}
              isAragonDAO={allocator.isAragonDAO}
            />
          </VaultV2RowListRow>
        )
      })}
    </VaultV2RowList>
  )
}

export default RiskCurationClient
